// Feature: M004-F0626 — the stored session survives a transient identity-read failure.
// Test:    WEB-SESSION-001 / WEB-SESSION-002 / WEB-SESSION-003.
// Canonical design: docs/design/M004-D001-RESTAPIs.md §"Client session bootstrap".
//
// WHY THIS MODULE EXISTS
//
// `AuthContext`'s boot used to treat EVERY failure of `GET /api/users/me` as proof
// that the stored token was invalid: one `catch (err)` around the call, then
// `clearAccessToken()`. A 401 means that. A 429, a 502, a TLS reset or a dropped
// connection does not — and each of those silently wiped a perfectly good session
// and dropped the user on the login form with no explanation, so nobody could
// report what had happened.
//
// That is not hypothetical. StudioAEye Web `main` pipelines 9074, 9082 and 9090
// (jobs 222873 / 222874) went red on it: the Apache access log on the TestDeploy
// host recorded
//
//   [09/Sep/2026:10:35:58 "GET /api/users/me 429
//
// immediately before the browser rendered the login screen, and then 46 seconds of
// total silence because the app had given up on a session that was never invalid.
// The deployed Core limit is 100 requests / IP / minute
// (studioaeye-backend src/middleware/rate-limit.ts:47-54, `RATE_LIMIT_MAX_REQUESTS`
// defaulting to 100 in src/config/index.ts:329).
//
// The classification below is deliberately CONSERVATIVE in one direction only: it
// clears the token when — and only when — the server said the credential itself was
// rejected. Anything it cannot positively identify as an authentication failure is
// treated as transient, because the cost of the two mistakes is not symmetric.
// Wrongly keeping a dead token costs one extra 401 on the next request, which the
// api.js response interceptor already handles. Wrongly discarding a live one logs a
// real person out of real work.

/** Attempts made before the bootstrap gives up and surfaces the failure. */
export const SESSION_BOOTSTRAP_MAX_ATTEMPTS = 3;

/** Upper bound on any single backoff wait, including a server-supplied Retry-After. */
export const SESSION_BOOTSTRAP_MAX_BACKOFF_MS = 4000;

/**
 * True only for a response that positively identifies the CREDENTIAL as rejected.
 *
 * 401 is the whole list. 403 is deliberately excluded: it means the session is real
 * and simply not permitted to do this, which is not a reason to destroy it.
 */
export function isSessionRejected(error) {
  return error?.response?.status === 401;
}

/**
 * True for a failure that says nothing about the credential: no response at all
 * (network/DNS/TLS/abort), a 408/425/429 throttle or timeout, or any 5xx.
 */
export function isTransientSessionFailure(error) {
  const status = error?.response?.status;

  // No response object at all: the request never reached a server that answered.
  if (status === undefined || status === null) {
    return true;
  }

  if (status === 408 || status === 425 || status === 429) {
    return true;
  }

  return status >= 500 && status <= 599;
}

/**
 * Backoff for the next attempt, honouring a server-supplied Retry-After but never
 * freezing the app behind it — a 429 carrying `retryAfter: 60` must not stall the
 * boot for a minute; the visible retry affordance is the right home for a wait that
 * long.
 */
export function sessionBootstrapBackoffMs(error, attempt) {
  const body = error?.response?.data || {};
  const header = error?.response?.headers?.['retry-after'] ?? error?.response?.headers?.['Retry-After'];
  const advertised = Number(body.retryAfter ?? body.retry_after ?? header);
  const advertisedMs = Number.isFinite(advertised) && advertised > 0 ? advertised * 1000 : 0;
  const exponential = 500 * (2 ** Math.max(0, attempt - 1));

  return Math.min(SESSION_BOOTSTRAP_MAX_BACKOFF_MS, Math.max(exponential, advertisedMs));
}

/**
 * The message the user actually sees. It names what happened and what the app is
 * doing about it; it never pretends the person is signed out, because they are not.
 */
export function sessionUnavailableMessage(error) {
  const status = error?.response?.status;

  if (status === 429) {
    return 'StudioAEye is rate-limiting this connection, so we could not confirm your session. You are still signed in — please retry in a moment.';
  }
  if (typeof status === 'number' && status >= 500) {
    return `StudioAEye could not confirm your session (server error ${status}). You are still signed in — please retry in a moment.`;
  }
  return 'We could not reach StudioAEye to confirm your session. You are still signed in — check your connection and retry.';
}
